import { FC, useEffect, useState } from "react";
import "./transactions.css";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { DigitalWalletOwnerInfoResponse, TransactionFilterRequest, TransactionRowResult } from "@/models/transaction";
import digitalWalletStore from "@/stores/digital.wallet.store";
import popupService from "@/services/popupService";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import { Grid } from "@mui/material";
import TransactionsFilter from "@/components/transactions/transactions.filter";
import TransactionsGrid from "@/components/transactions/transactions.grid";
import { useParams } from "react-router-dom";
import DigitalWalletOwnerInfo from "@/components/transactions/digital.wallet.owner.info";
import { useTranslation } from "react-i18next";
import TotalCountComponent from "@/components/common/resultCountComponent";
import TransactionsInfoTab from "@/components/transactions/transactions.info.tab";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const TransactionsPage: FC = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [transactions, setTransactions] = useState<TransactionRowResult[]>([]);
    const [walletOwnerInfo, setWalletOwnerInfo] = useState<DigitalWalletOwnerInfoResponse>(
        new DigitalWalletOwnerInfoResponse()
    );
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionRowResult | null>(null);

    const [filter, setFilter] = useState<TransactionFilterRequest>({
        walletId: Number(id),
        keywords: "",
        amount: null,
        fromDate: null,
        tillDate: null,
        transactionType: null,
        walletStatus: null,
        transactionId: "",
        employeeStatus: null,
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });

    async function startFetching() {
        setIsSearch(() => true);
        try {
            var result = await digitalWalletStore.getTransactions(filter);
            if (result) {
                setHasNext(result.hasNext);
                if (filter.pageIndex === 0) {
                    setWalletOwnerInfo(result.digitalWalletOwnerInfo);
                    setTransactions(result.rows);
                    setTotal(result.total);
                    setTotalFilter(result.totalFilter);
                } else {
                    setTransactions((old) => [...old, ...result.rows]);
                }
                setIsSearch(() => false);
            }
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    }

    const searchTransactionsClick = () => {
        setTransactions([]);
        setPageIndex(0);
        setSearchCounter(searchCounter + 1);
    };

    const setPageIndex = (index?: number) => {
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? filter.pageIndex + 1,
        }));
    };

    async function sortBy(sortBy: string) {
        if (filter.orderBy !== sortBy) {
            setFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
            }));
        } else {
            setFilter((prevData) => ({
                ...prevData,
                asc: !filter.asc,
                orderBy: sortBy,
            }));
        }
        setPageIndex(0);
        setTransactions([]);
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [filter.pageIndex, filter.orderBy, filter.asc, searchCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <Grid className="transactions-main-block">
                {selectedTransaction ? (
                    <TransactionsInfoTab
                        selectedTransaction={selectedTransaction}
                        walletInfo={walletOwnerInfo}
                        setSelectedTransaction={setSelectedTransaction}
                    />
                ) : (
                    <DigitalWalletOwnerInfo info={walletOwnerInfo}></DigitalWalletOwnerInfo>
                )}

                <TransactionsFilter
                    searchClick={searchTransactionsClick}
                    filter={filter}
                    setFilter={setFilter}
                ></TransactionsFilter>
                <TotalCountComponent
                    allCount={total}
                    currentCount={totalFilter}
                    title={t("digitalWallet.QuantityOfTransactions") as string}
                />
                <TransactionsGrid
                    sortBy={sortBy}
                    transactions={transactions}
                    isSearch={isSearch}
                    fetchMore={setPageIndex}
                    hasNext={hasNext}
                    info={walletOwnerInfo}
                    orderBy={filter.orderBy}
                    asc={filter.asc}
                    setSelectedTransaction={setSelectedTransaction}
                ></TransactionsGrid>
            </Grid>
        </CacheProvider>
    );
};

export default TransactionsPage;
